import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Trash2, Plus, Key, Search } from "lucide-react";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function Accounts() {
  const [accounts, setAccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [name, setName] = useState("");
  const [vkn, setVkn] = useState("");
  
  const [passModalOpen, setPassModalOpen] = useState(false);
  const [selectedAcc, setSelectedAcc] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchAccounts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/accounts`, { withCredentials: true });
      setAccounts(data);
    } catch (e) {
      toast.error("Cari hesaplar alınamadı.");
    }
  }, [BACKEND_URL]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await axios.post(`${BACKEND_URL}/api/accounts`, { name, vkn: vkn.trim() || null }, { withCredentials: true });
      toast.success("Hesap oluşturuldu.");
      setName("");
      setVkn("");
      fetchAccounts();
    } catch (e) {
      toast.error("Oluşturulamadı.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/accounts/${id}`, { withCredentials: true });
      toast.success("Hesap silindi.");
      fetchAccounts();
    } catch (e) {
      toast.error("Silinemedi.");
    }
  };
  
  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword.trim()) return;
    try {
      await axios.post(`${BACKEND_URL}/api/accounts/${selectedAcc.id}/credentials`, { password: newPassword }, { withCredentials: true });
      toast.success(`Şifre belirlendi. Müşteri kullanıcı adı: ${selectedAcc.name}`);
      setPassModalOpen(false);
      setNewPassword("");
    } catch (err) {
      toast.error("Şifre belirlenemedi.");
    }
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <div className="flex items-center justify-between border-b border-border pb-4">
        <h1 className="text-3xl font-heading font-bold">Cari Hesaplar</h1>
      </div>

      <Card className="swiss-card p-6">
        <form onSubmit={handleCreate} className="flex gap-4 items-end flex-wrap">
          <div className="flex-1 space-y-2 min-w-[200px]">
            <Label htmlFor="account-name">Yeni Hesap Adı / Kişi</Label>
            <Input
              id="account-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Örn: Ahmet Yılmaz veya X Şirketi"
              data-testid="account-name-input"
            />
          </div>
          <div className="flex-1 space-y-2 min-w-[200px]">
            <Label htmlFor="account-vkn">VKN / TCKN</Label>
            <Input
              id="account-vkn"
              value={vkn}
              onChange={(e) => setVkn(e.target.value)}
              placeholder="11 Haneli TC veya 10 Haneli VKN"
            />
          </div>
          <Button type="submit" data-testid="create-account-button">
            <Plus size={16} className="mr-2" /> Ekle
          </Button>
        </form>
      </Card>

      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Cari hesaplarda ara... (İsim veya VKN)"
          className="pl-9 bg-white"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {accounts
          .filter(acc => 
            (acc.name || "").toLowerCase().includes(searchTerm.toLowerCase()) || 
            (acc.vkn || "").includes(searchTerm)
          )
          .map(acc => (
          <Card key={acc.id} className="swiss-card flex items-center justify-between p-4">
            <div className="flex flex-col">
              <span className="font-medium" data-testid={`account-name-${acc.id}`}>{acc.name}</span>
              {acc.vkn && <span className="text-xs text-muted-foreground font-mono">VKN: {acc.vkn}</span>}
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-primary hover:bg-primary/10"
                title="Müşteri Girişi İçin Şifre Belirle"
                onClick={() => { setSelectedAcc(acc); setPassModalOpen(true); }}
                data-testid={`set-password-${acc.id}`}
              >
                <Key size={18} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive hover:bg-destructive/10"
                onClick={() => {
                  toast("Emin misiniz?", {
                    action: {
                      label: "Evet, Sil",
                      onClick: () => handleDelete(acc.id)
                    }
                  });
                }}
                data-testid={`delete-account-button-${acc.id}`}
              >
                <Trash2 size={18} />
              </Button>
            </div>
          </Card>
        ))}
        {accounts.length === 0 && (
          <div className="text-muted-foreground text-sm col-span-full">Kayıtlı hesap bulunamadı.</div>
        )}
      </div>

      <Dialog open={passModalOpen} onOpenChange={setPassModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Müşteri Erişimi Şifresi Belirle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSetPassword} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Kullanıcı Adı</Label>
              <Input value={selectedAcc?.name || ""} disabled className="bg-neutral-100 font-medium" />
              <p className="text-xs text-muted-foreground">Müşteriniz sisteme bu kullanıcı adıyla (büyük/küçük harf duyarlı) giriş yapacaktır. Boşluklar dahil aynen yazılmalıdır.</p>
            </div>
            <div className="space-y-2">
              <Label>Yeni Şifre</Label>
              <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required data-testid="new-password-input" />
            </div>
            <Button type="submit" className="w-full h-10 mt-2" data-testid="save-password-btn">Şifreyi Kaydet</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
